import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Apple, ArrowRight, Utensils, BarChart3, MapPin, Calendar, FileText, Leaf } from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_healthykids-5/artifacts/v5g7yp6u_NINF-Image.png",
  nutrition: "https://images.unsplash.com/photo-1597058712635-3182d1eacc1e?w=800&q=80",
  child: "https://images.unsplash.com/photo-1595265185654-f7b3c41c9a57?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

const features = [
  { icon: BarChart3, title: "ICMR-NIN 2020 RDA Analysis", description: "Nutritional requirements calculated using the latest Indian Council of Medical Research (ICMR) and National Institute of Nutrition (NIN) 2020 Recommended Dietary Allowances — the gold standard for Indian children.", color: "#FF6B6B" },
  { icon: MapPin, title: "Region-Specific Food Plans", description: "Meal plans tailored to 6 Indian regions — South, North, East, West, Central, and North East India — using locally available, culturally appropriate ingredients with IFCT 2017 food codes.", color: "#4ECDC4" },
  { icon: Calendar, title: "7-Day & 30-Day Meal Plans", description: "Complete daily, weekly, and monthly meal plan calendars with 5 meals per day (breakfast, snacks, lunch, dinner) and full calorie/protein breakdowns.", color: "#FFE66D" },
  { icon: Leaf, title: "Vitamin & Mineral Profiling", description: "Detailed breakdown of water-soluble vitamins (B-complex, C), fat-soluble vitamins (A, D, E, K), minerals (calcium, magnesium), and trace elements (iron, zinc, iodine).", color: "#A855F7" },
  { icon: Utensils, title: "Dietary Preference Support", description: "Full support for vegetarian, non-vegetarian, and mixed diets. Intelligent filtering ensures meal plans respect your family's food preferences.", color: "#FF6B6B" },
  { icon: FileText, title: "Clinical PDF Reports", description: "Generate comprehensive nutrition reports with growth comparison charts, macronutrient pie charts, vitamin bar charts, and personalized recommendations.", color: "#4ECDC4" },
];

const howItWorks = [
  { step: "01", title: "Enter Child Profile", description: "Provide your child's age, height, weight, bone age, gender, food preference, and region." },
  { step: "02", title: "AI Nutrition Analysis", description: "Our engine cross-references NIN RDA data (510+ records) with your child's growth metrics." },
  { step: "03", title: "Get Personalized Plans", description: "Receive regional meal plans, food recommendations, and micronutrient targets." },
  { step: "04", title: "Download Report", description: "Get a branded clinical PDF with charts, recommendations, and 30-day meal calendar." },
];

const NINNutriGuideAboutPage = () => {
  useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="pt-28 pb-16">
        {/* Hero with NINF-Image */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center bg-[#FF6B6B]/10 border border-[#FF6B6B]/20">
                <Apple className="w-8 h-8 text-[#FF6B6B]" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4" data-testid="nutriguide-about-title">
                NIN <span className="text-[#FF6B6B]">Nutri</span>Guide
              </h1>
              <p className="text-lg text-white/50 mb-4">AI-Powered Nutrition Analysis Based on ICMR-NIN Standards</p>
              <p className="text-base text-white/40 leading-relaxed mb-8">
                India's first child nutrition platform powered by the complete ICMR-NIN 2020 RDA dataset.
                It generates personalized nutrition plans with region-specific Indian meals, detailed micronutrient profiling,
                and growth-adjusted dietary recommendations — ensuring every child gets exactly what they need to thrive.
              </p>
              <Link to="/tools/nin-nutriguide" className="inline-flex items-center gap-2 bg-[#FF6B6B] hover:bg-[#e05555] text-white font-semibold px-8 py-4 rounded-xl text-lg transition-colors" data-testid="nutriguide-try-now-btn">
                Try NutriGuide Now <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.hero} alt="NIN-NutriGuide Platform" className="w-full h-80 object-cover rounded-xl" data-testid="nutriguide-hero-image" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Science-backed nutrition with image */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.nutrition} alt="Child nutrition and health monitoring" className="w-full h-72 object-cover rounded-xl" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <span className="badge-glass inline-flex px-4 py-1.5 rounded-full text-xs font-semibold text-[#FF6B6B] mb-4 uppercase tracking-wider">
                Science-Backed
              </span>
              <h2 className="font-display text-3xl text-white mb-4">Indian Meals, Backed by Science</h2>
              <p className="text-white/50 leading-relaxed mb-4">
                Our meal plans are built from traditional Indian recipes sourced from 6 regions, mapped to the IFCT 2017
                food composition database. Every ingredient is selected for its nutritional density and local availability.
              </p>
              <div className="space-y-3">
                {["South Indian: Ragi, idli, sambar, drumstick", "North Indian: Paratha, dal, paneer, seasonal veggies", "Regional ingredients with IFCT food codes", "Vegetarian, non-veg, and mixed diet support"].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#FF6B6B]/20 flex items-center justify-center shrink-0">
                      <div className="w-2 h-2 rounded-full bg-[#FF6B6B]" />
                    </div>
                    <span className="text-sm text-white/60">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Key Data Points */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: "510+", label: "NIN RDA Data Records", color: "#FF6B6B" },
              { value: "6", label: "Indian Regions Covered", color: "#4ECDC4" },
              { value: "30", label: "Day Meal Plan Calendar", color: "#FFE66D" },
              { value: "40+", label: "Micronutrients Tracked", color: "#A855F7" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="glass-card p-6 text-center">
                <p className="text-3xl font-bold font-display mb-1" style={{ color: s.color }}>{s.value}</p>
                <p className="text-xs text-white/50">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Features with child image */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <h2 className="font-display text-3xl text-white mb-3">What NIN-NutriGuide Offers</h2>
              <p className="text-white/40 mb-8">Complete nutrition intelligence for Indian children</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                    className="glass-card p-5" data-testid={`nutriguide-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${f.color}15` }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <h3 className="font-display text-sm text-white mb-1">{f.title}</h3>
                    <p className="text-xs text-white/45 leading-relaxed">{f.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <motion.div className="lg:col-span-2 hidden lg:block" initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden sticky top-32">
                <img src={IMAGES.child} alt="Child eating healthy food" className="w-full h-[500px] object-cover rounded-xl" loading="lazy" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">How It Works</h2>
            <p className="text-white/40">Four steps to personalized nutrition</p>
          </div>
          <div className="space-y-5">
            {howItWorks.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-start gap-5 glass-card p-5" data-testid={`nutriguide-step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#FF6B6B]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#FF6B6B]">{s.step}</span>
                </div>
                <div>
                  <h3 className="font-display text-lg text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-white/50">{s.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Regions */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="glass-card p-10 border border-[#FF6B6B]/20">
            <h2 className="font-display text-2xl text-white mb-6 text-center">Regions We Cover</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {["South India", "North India", "East India", "West India", "North East India", "Central India"].map((r, i) => (
                <div key={i} className="text-center p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <MapPin className="w-5 h-5 text-[#FF6B6B] mx-auto mb-2" />
                  <p className="text-sm text-white/70 font-medium">{r}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/tools/nin-nutriguide" className="inline-flex items-center gap-2 bg-[#FF6B6B] hover:bg-[#e05555] text-white font-semibold px-10 py-4 rounded-xl text-lg transition-colors">
            Start Nutrition Analysis <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NINNutriGuideAboutPage;
